import { formatDatum } from "../../format.js";
import { effortShare, hasEffort, hoursNumber, type ShownEffort } from "./effort.js";

/** Alle Anwendertexte des Aufwands (Spec „Texte“). */
const planPart = (plan: number | null): string => (plan === null ? "–" : hoursNumber(plan));
const sharePercent = (plan: number | null, worked: number): number | null => effortShare(plan, worked)?.percent ?? null;

export const EFFORT_TEXT = {
  section: "Aufwand", book: "Aufwand buchen", empty: "Noch kein Aufwand gebucht.",
  estimate: "Schätzung", estimateName: "Schätzung in Stunden", none: "–",
  hours: (m: number) => `${hoursNumber(m)} h`, percent: (p: number) => `${p} %`,
  day: (iso: string) => formatDatum(iso), shortDay: (iso: string) => formatDatum(iso).slice(0, 6),
  mark: (plan: number | null, worked: number, sum: boolean) =>
    `${sum ? "Σ " : ""}${planPart(plan)} · ${hoursNumber(worked)} h`,
  line: (plan: number | null, worked: number): string => {
    const p = sharePercent(plan, worked);
    const ist = `Ist ${hoursNumber(worked)} h`;
    return plan === null ? ist : `Plan ${hoursNumber(plan)} h · ${ist}${p === null ? "" : ` (${p} %)`}`;
  },
  own: (line: string) => `Eigenes: ${line}`, ownPopup: (line: string) => `davon eigenes: ${line}`,
  sum: (line: string) => `Inkl. Teilaufgaben: ${line}`, sumPopup: (line: string) => `inkl. Teilaufgaben: ${line}`,
  workedChip: (worked: number, p: number | null) => `Ist ${hoursNumber(worked)} h${p === null ? "" : ` · ${p} %`}`,
  spoken: (plan: number | null, worked: number): string => {
    const p = sharePercent(plan, worked);
    if (plan === null) return `Aufwand ${hoursNumber(worked)} Stunden`;
    return `Aufwand ${hoursNumber(worked)} von ${hoursNumber(plan)} Stunden${p === null ? "" : `, ${p} Prozent`}`;
  },
  columns: { date: "Datum", person: "Person", hours: "Stunden", note: "Bemerkung" },
  panelBook: (nr: string) => `Aufwand buchen · ${nr}`, panelDelete: "Buchung löschen", bookedAt: "gebucht am",
  hoursHint: "in Viertelstunden, z. B. 1,25", rounded: "auf Viertelstunden gerundet", minQuarter: "Mindestens 0,25 h.",
  unreadable: "Bitte Stunden als Zahl eingeben, z. B. 1,5.",
  deleteName: (day: string, minutes: number) => `Buchung vom ${formatDatum(day).slice(0, 6)} (${hoursNumber(minutes)} h) löschen`,
  alreadyGone: "Diese Buchung war schon gelöscht.", failLoad: "Die Buchungen ließen sich nicht laden.",
  history: {
    estimate: (from: number | null, to: number | null) =>
      `Schätzung: ${from === null ? "keine" : `${hoursNumber(from)} h`} → ${to === null ? "keine" : `${hoursNumber(to)} h`}`,
    logged: (minutes: number, day: string) => `Aufwand gebucht: ${hoursNumber(minutes)} h am ${formatDatum(day)}`,
    removed: (minutes: number, day: string, name: string | null) =>
      `Buchung gelöscht: ${hoursNumber(minutes)} h am ${formatDatum(day)}${name === null ? "" : ` (${name})`}`,
  },
  legendShare: "Ist/Plan", legendOverrun: "über Plan",
  /** Vorlesestimme am Eltern-Balken (570, Review G2): die Σ-Werte ausdrücklich als Teilbaum. */
  spokenSum: (plan: number | null, worked: number): string => `${EFFORT_TEXT.spoken(plan, worked)}, inkl. Teilaufgaben`,
} as const;

/** Zeilen für Sofort-Popup (inkl. zuerst) und Dialog (eigenes zuerst); ohne Werte keine. Eltern ohne eigene Werte
 *  zeigen nur die Σ-Zeile (Ruling Controller zu Review Aufgabe 4 G2). */
export function effortLines(s: ShownEffort, where: "popup" | "dialog"): string[] {
  if (!hasEffort(s)) return [];
  const all = EFFORT_TEXT.line(s.plan, s.worked);
  if (!s.sum) return [all];
  const sum = where === "popup" ? EFFORT_TEXT.sumPopup(all) : EFFORT_TEXT.sum(all);
  if (!hasEffort(s.own)) return [sum];   // 570: Eltern ohne eigene Werte zeigen nur die Σ-Zeile
  const own = EFFORT_TEXT.line(s.own.plan, s.own.worked);
  return where === "popup" ? [sum, EFFORT_TEXT.ownPopup(own)] : [EFFORT_TEXT.own(own), sum];
}
